import { useCallback } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { patchProcess, type ProcessDetailDto } from '@api/processes';
import { processDetailQueryKey } from './useProcessDetail';
import { processesListRootKey } from './useProcesses';
import type { ProcessPublishAutoSave } from './useProcessPublish';

/**
 * Auto-save shape required by the rename flow — a superset of
 * {@link ProcessPublishAutoSave} that also accepts a `name` override on flush
 * so the rename can ride along with the regular PUT (avoids a side-channel
 * PATCH that would desync auto-save's `lastSavedAt` token).
 */
export interface ProcessRenameAutoSave extends ProcessPublishAutoSave {
  flush: (
    overrides?: { name?: string },
  ) => Promise<ProcessDetailDto | undefined>;
  adoptExternalSave: () => void;
  cancelExternalSaveAdoption: () => void;
}

export interface UseProcessRenameParams {
  processId: string | undefined;
  detail: ProcessDetailDto | null | undefined;
  autoSave: ProcessRenameAutoSave;
  /** False while the process is published or a run is active; flush() then saves nothing. */
  autoSaveEnabled: boolean;
  queryClient: QueryClient;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

export interface UseProcessRenameResult {
  /**
   * Inline rename handler — applies an optimistic cache write, persists the
   * new name, invalidates the gallery list, and rolls the cache back on
   * failure (with a snackbar).
   *
   * No-ops when the trimmed value is empty or unchanged so callers can
   * wire it directly to a blur / Enter handler without extra guards.
   * Resolves false when the save failed.
   */
  onCommitRename: (newName: string) => Promise<boolean>;
}

/**
 * Inline rename from the breadcrumb. While auto-save is enabled we fold the
 * rename into the regular auto-save PUT instead of sending a parallel PATCH
 * so the optimistic-lock token (`expectedUpdatedAt`) advances along a single
 * path and pending canvas edits are persisted in the same round-trip.
 *
 * While auto-save is disabled there is no PUT to ride on, so the name goes
 * out as a PATCH and auto-save adopts the version it creates.
 */
export const useProcessRename = ({
  processId,
  detail,
  autoSave,
  autoSaveEnabled,
  queryClient,
  showSuccess,
  showError,
}: UseProcessRenameParams): UseProcessRenameResult => {
  const persistName = useCallback(
    async (id: string, name: string) => {
      if (autoSaveEnabled) {
        await autoSave.flush({ name });
        return;
      }
      autoSave.adoptExternalSave();
      try {
        const fresh = await patchProcess(id, { name });
        queryClient.setQueryData(processDetailQueryKey(id), fresh);
      } catch (err) {
        autoSave.cancelExternalSaveAdoption();
        throw err;
      }
    },
    [autoSaveEnabled, autoSave, queryClient],
  );

  const onCommitRename = useCallback(
    async (newName: string) => {
      if (!processId || !detail) return true;
      const trimmed = newName.trim();
      const original = detail;
      if (!trimmed || trimmed === original.name) return true;
      queryClient.setQueryData<ProcessDetailDto>(
        processDetailQueryKey(processId),
        (prev) => (prev ? { ...prev, name: trimmed } : prev),
      );
      try {
        await persistName(processId, trimmed);
        queryClient
          .invalidateQueries({ queryKey: processesListRootKey })
          .catch(() => undefined);
        showSuccess('Process renamed.');
        return true;
      } catch (err) {
        queryClient.setQueryData<ProcessDetailDto>(
          processDetailQueryKey(processId),
          (prev) => (prev ? { ...prev, name: original.name } : prev),
        );
        const msg =
          err instanceof Error ? err.message : 'Failed to rename process';
        showError(msg);
        return false;
      }
    },
    [processId, detail, queryClient, persistName, showSuccess, showError],
  );

  return { onCommitRename };
};
